import _ from 'lodash';
import WebStoragePolyfill from '../../data-providers/webstorage-polyfill';
import Analytics from '../../analytics';
import actions from '../../actions/product';

export default [
  '$scope', '$q', '$window', '$sce', '$interval', '$state', '$filter',
  'CampaignService', 'ResourceService', 'ErrorHandler', 'CurrencyService',
  class UpsellCtrl {
    constructor($scope, $q, $window, $sce, $interval, $state, $filter,
      CampaignService, ResourceService, ErrorHandler, CurrencyService, webstorage = new WebStoragePolyfill(), analytics = new Analytics()) {
      this._$scope = $scope;
      this._$q = $q;
      this._$window = $window;
      this._$sce = $sce;
      this._$interval = $interval;
      this._$state = $state;
      this._$filter = $filter;
      this._campaignService = CampaignService;
      this._resourceService = ResourceService;
      this._errorHandler = ErrorHandler;
      this._storage = webstorage;
      this.campaign = null;
      this.processing = false;
      this.discount = 0;
      this.formattedProduct = { quantity: 1, qty: 1 };
      this.previousUpsells = [];
      this.endTime = {};
      this.ended = false;
      this.currency = CurrencyService;
      this.analytics = analytics;
    }

    init(campaignID, country) {
      this.disableButton = false;
      this.upsellStorage = `upsells-${campaignID}`;
      this.previousUpsells = JSON.parse(this._storage.getLocalItem(this.upsellStorage)) || [];
      if (!Array.isArray(this.previousUpsells)) this.previousUpsells = [];

      let deferred = this._$q.defer();

      if (this.previousUpsells.length < 2) {
        this._campaignService
          .getUpsells(campaignID, this.previousUpsells, country)
          .then(data => {
            this.discount = data.discount;
            this.campaign = data.campaign;
            this.campaign.products = actions.filterInactiveProducts(this.campaign.products);
            this.previousUpsells.push(data.id);
            this.campaignsProductId = data.campaigns_product_id;
            this.defaultSide = data.default_side;
            this.shipping = data.upsell_shipping_cost;

            this._setCountdown();
            this._setDefaultSelections();
            this._watchForGroupChange();
            this.viewedUpsell();

            deferred.resolve();
          });
      } else {
        deferred.reject();
      }

      return deferred.promise;
    }

    campaignDescription() {
      return this._$sce.trustAsHtml(this.campaign.description);
    }

    paypalCheckout() {
      if (!this.selectedSize) {
        this.upsellSubmitted = true;
      } else {
        this.disableButton = true;
      }
    }

    checkout() {
      if (!this.selectedSize) {
        this.upsellSubmitted = true;
      } else {
        if (this.paymentMethod !== 'STRIPE' && this.paymentMethod !== 'BRAINTREE' && this.paymentMethod !== 'WORLDPAY') return;
        if (this.disableButton) return;
        this.disableButton = true;

        let method;
        switch (this.paymentMethod) {
          case 'STRIPE':
            method = 'Stripe';
            break;
          case 'BRAINTREE':
            method = 'Braintree';
            break;
          case 'WORLDPAY':
            method = 'Worldpay';
            break;
        }

        this.analytics.checkout({ option: method, upsell: true })

        let payload = {
          product_id: this.selectedProduct.id,
          size: this.selectedSize,
          quantity: 1,
          discount: this.discount,
          campaign_id: this.campaignId,
          upsell_campaign_id: this.campaign.id,
          original_order_id: this.orderId
        };

        this._resourceService
          .legacyGet('/token')
          .then((res) => payload._token = res.token)
          .then(() => this._resourceService.legacyPost('/purchase/checkout-upsell', payload))
          .then((res) => {
            this._storage.removeLocalItem('order-credentials');
            this._storage.setLocalItem('order-credentials', `${res.order_num}:${res.customer.zip}`);
            this.analytics.placeOrder({ option: method, upsell: true });

            this._$state.go('thank-you', {
              password: null //prevents duplicate login
            }, { reload: true });
          })
          .catch(() => {
            this._errorHandler.throwError('Order Error', 'There was an error adding this item to your order.');
            this.disableButton = false;
          });
      }
    }

    formatSelectedProduct() {
      this.formattedProduct.cid = this.campaign.id;
      this.formattedProduct.product_id = this.selectedProduct.id;
      this.formattedProduct.cpid = this.selectedProduct.id;
      this.formattedProduct.size = this.selectedSize;
      this.formattedProduct.name = this.selectedProduct.product.name;
      this.formattedProduct.thumbnail = this.selectedProduct.assets.front_thumbnail;

      if (this.selectedProduct.color.name != null && this.selectedProduct.color.name != '') {
        this.formattedProduct.name = this.selectedProduct.color.name + ' ' + this.formattedProduct.name;
      }

      return this.formattedProduct;
    }

    productGroups() {
      let unique = _.uniq(this.campaign.products, (p) => p.product.id);
      let groups = [];

      _.forEach(unique, (u) => {
        let displayPrice = this.productDiscountedPrice(u) * this.currency.info.conversion_rate;

        groups.push({
          label: `${u.product.name} - ${this._$filter('currency')(displayPrice, this.currency.info.currency_symbol)}`,
          id: u.product.id
        });
      });

      return groups;
    }

    productColors() {
      let productID = this.selectedProduct.product.id;
      let products = _.filter(this.campaign.products, (p) => _.has(p, 'product.id') && p.product.id === productID);

      return products;
    }

    productSizes() {
      return this.selectedProduct.product.sizes;
    }

    productPrice(product) {
      let p = product || this.selectedProduct;
      let upcharge = p.product.upcharges[this.selectedSize] || 0;
      return parseFloat(p.selling_price) + parseFloat(upcharge);
    }

    productDiscountedPrice(product) {
      return this.productPrice(product) - parseFloat(this.discount);
    }

    productImage(product) {
      let p = product || this.selectedProduct;
      if (this.defaultSide === 'BACK') {
        return p.assets.back_url;
      }
      return p.assets.front_url;
    }

    _setCountdown() {
      let end = moment(new Date()).add(5, 'minutes');
      this._setCountdownDisplay(end);

      this.countdown = this._$interval(() => {
        let now = moment(new Date());
        if (end - now <= 0) {
          this._$interval.cancel(this.countdown);
          this.ended = true;
          if (typeof this.closeUpsell === 'function') {
            this.closeUpsell();
          }
          return;
        }
        this._setCountdownDisplay(end);

      }, 251);
    }

    stopCountdown() {
      if (this.countdown) {
        this._$interval.cancel(this.countdown);
      }
    }

    _setCountdownDisplay(end) {
      let now = moment(new Date());

      let days = (end - now) / (1000 * 60 * 60 * 24);
      let hours = (days - Math.floor(days)) * 24;
      let minutes = (hours - Math.floor(hours)) * 60;
      let seconds = (minutes - Math.floor(minutes)) * 60;

      this.ended = false;
      this.endTime.days = Math.floor(days);
      this.endTime.hours = Math.floor(hours);
      this.endTime.minutes = Math.floor(minutes);
      this.endTime.seconds = Math.floor(seconds);

      if (this.endTime.seconds < 10) {
        this.endTime.seconds = `0${this.endTime.seconds}`;
      }
    }

    _setDefaultSelections() {
      this.selectedProduct = actions.defaultUpsellProduct(this.campaign.products, this.campaignsProductId);
      this.selectedSize = this.selectedProduct.product.sizes.length > 1 ? null : this.selectedProduct.product.sizes[0];
      this.selectedGroup = _.find(this.productGroups(), (group) => group.id === this.selectedProduct.product.id);
    }

    _watchForGroupChange() {
      this._$scope.$watch(() => this.selectedGroup, (group, oldGroup) => {
        if (group.id === oldGroup.id) return;

        this.selectedProduct = _.find(this.campaign.products, (product) => product.product.id === group.id);
        if (!_.find(this.productSizes(), this.selectedSize)) {
          this.selectedSize = this.selectedProduct.product.sizes.length > 1 ? null : this.selectedProduct.product.sizes[0];
        }
      });
    }

    _preferredProduct() {
      let preferred = _.find(this.campaign.products, (p) => _.includes(this.preferredProducts(), p.product.id));
      if (!preferred) preferred = actions.defaultUpsellProduct(this.campaign.products, this.campaignsProductId);
      return preferred;
    }

    viewedUpsell() {
      this._storage.setLocalItem(this.upsellStorage, JSON.stringify(this.previousUpsells));
    }

    isProductClock() {
      return actions.isProductClock(this.selectedProduct.product);
    }

    sizeDisplay(size) {
      return actions.sizeDisplay(this.selectedProduct.product, size);
    }
  }
];


// WEBPACK FOOTER //
// ./source/client/pages/upsell/upsell.ctrl.js